export type Team = 'A' | 'B';
export type ResultKind = 'do' | 'gae' | 'geol' | 'yut' | 'mo' | 'backdo';
export type GameMode = 'vanilla' | 'augment';
export type CardKind = 'chase' | 'shortcut' | 'retreat' | 'smoke' | 'delay' | 'breakthrough' | 'push' | 'curse' | 'rescue';
export type TerrainKind = 'lightning' | 'collapse' | 'gale';
export interface CardSlot { card?: CardKind; choices?: CardKind[]; offerId?: string; choiceDeadline?: number; refillAt?: number }
export interface Battlefield { kind: TerrainKind; nodes: string[]; activatesRound: number; expiresRound?: number }
export interface CurseLink { id: string; sourceIds: string[]; targetIds: string[]; expiresRound: number; armedTurn: number }
export interface CardAction { key: string; card: CardKind; sourceId?: string; targetId?: string; to?: string; path?: string[]; label: string }
export interface CardUse { playerId: string; card: CardKind }
export interface Clash { move: Move; stage: 'defense' | 'counter'; defense?: CardUse & { action?: CardAction } }
export interface AugmentState {
  hands: Record<string, CardSlot>; completedTurns: Record<Team, number>; completedRounds: number;
  forecast?: Battlefield; terrain?: Battlefield; lastEffect?: Battlefield & { at: number };
  curses: CurseLink[]; moveCard?: CardUse; utilityTurn?: number; clash?: Clash;
}
export interface AugmentView extends Omit<AugmentState, 'hands' | 'moveCard'> {
  hands: Record<string, CardSlot>; moveCard?: CardUse; cardActions?: CardAction[];
}
export type Phase = 'throw' | 'vote' | 'response' | 'animating' | 'finished';
export interface Piece { id: string; team: Team; position: string; history: string[] }
export interface YutResult { id: string; kind: ResultKind; steps: number; sticks: boolean[] }
export interface Move { key: string; resultId: string; kind: ResultKind; pieceIds: string[]; from: string; to: string; path: string[]; route: string; modifier?: string }
export interface Proposal { move: Move; voters: string[]; order: number }
export interface GameEvent { id: number; text: string; type: 'throw' | 'move' | 'capture' | 'stack' | 'finish' | 'turn' | 'notice' | 'card' | 'environment'; at: number; path?: string[]; pieceIds?: string[] }
export interface GameState {
  id: string; pieces: Piece[]; results: YutResult[]; phase: Phase; activeTeam: Team;
  order: Record<Team, string[]>; throwIndex: Record<Team, number>; throwerId: string;
  phaseId: number; deadline: number; proposals: Proposal[]; events: GameEvent[];
  winner?: Team; startedAt: number; endedAt?: number; turn: number; moves: number;
  nextAction?: 'throw' | 'vote' | 'turn'; lastThrow?: YutResult;
  animationKind?: 'throw' | 'move';
  augment?: AugmentState;
}
export interface Player { id: string; nickname: string; team: Team; ready: boolean; connected: boolean; disconnectedAt?: number; kicked?: boolean; bot?: boolean }
export interface RoomState { id: string; name: string; hostId: string; mode?: GameMode; players: Player[]; status: 'waiting' | 'playing' | 'finished'; game?: GameState; createdAt: number; emptySince?: number; botDueAt?: number }
export interface LobbyRoom { id: string; name: string; mode?: GameMode; bots?: number; count: number; ready: number; status: RoomState['status']; locked: boolean; joinable: boolean }
export interface LobbyView { rooms: LobbyRoom[]; maxRooms: number; creationBlocked: boolean }
export interface GameView extends Omit<GameState, 'proposals' | 'augment'> { proposals?: Proposal[]; legalMoves?: Move[]; augment?: AugmentView }
export interface RoomView extends Omit<RoomState, 'game'> { game?: GameView; locked: boolean }
export interface ChatMessage { id: string; playerId: string; nickname: string; channel: 'team' | 'all'; text: string; at: number }
export interface PingMessage { id: string; playerId: string; nickname: string; kind: 'here' | 'danger' | 'capture' | 'agree'; target: string; at: number; expiresAt: number }
export type ClientCommand =
 | { type: 'hello'; token?: string; nickname: string }
 | { type: 'lobby' }
 | { type: 'create'; name: string; password?: string; mode?: GameMode }
 | { type: 'join'; roomId: string; password?: string }
 | { type: 'quickJoin'; mode?: GameMode }
 | { type: 'bots'; action: 'fill' | 'remove' }
 | { type: 'chooseCard'; gameId: string; offerId: string; card: CardKind }
 | { type: 'useCard'; gameId: string; phaseId: number; actionKey: string }
 | { type: 'leave' }
 | { type: 'team'; team: Team }
 | { type: 'ready'; ready: boolean }
 | { type: 'start' }
 | { type: 'rematch' }
 | { type: 'throw'; gameId: string; phaseId: number; timing?: number }
 | { type: 'propose'; gameId: string; phaseId: number; moveKey: string }
 | { type: 'vote'; gameId: string; phaseId: number; moveKey: string }
 | { type: 'chat'; channel: 'team' | 'all'; text: string }
 | { type: 'ping'; kind: PingMessage['kind']; target: string }
 | { type: 'kick'; playerId: string };
export type ServerMessage =
 | { type: 'welcome'; playerId: string; token: string; serverTime: number }
 | { type: 'lobby'; data: LobbyView; serverTime: number }
 | { type: 'room'; data: RoomView; serverTime: number }
 | { type: 'chat'; data: ChatMessage }
 | { type: 'ping'; data: PingMessage }
 | { type: 'left'; reason?: string }
 | { type: 'error'; message: string; code?: string };
export const TEAM_NAMES: Record<Team, string> = { A: '해님', B: '달님' };
export const RESULT_NAMES: Record<ResultKind, string> = { do: '도', gae: '개', geol: '걸', yut: '윷', mo: '모', backdo: '빽도' };
export const DEFAULT_TIMING = { throwMs: 10_000, voteMs: 12_000, animationMs: 1_200, singleMoveMs: 1_000, throwAnimationMs: 1_800 };
export type Timing = typeof DEFAULT_TIMING;
